// src/Components/PremiumSystem.jsx
import React, { useState } from 'react';
import { Crown, X, Check, Sparkles, Zap, Star, Lock } from 'lucide-react';
import StripePaymentModal from './StripeCheckout';

import { 
  gradients, 
  buttonStyles, 
  cardStyles, 
  shadows 
} from '../shared/config/theme';

// ============================================================
// PREMIUM UPGRADE MODAL
// ============================================================
export const PremiumUpgradeModal = ({ isOpen, onClose, feature = null, user, onUpgradeSuccess }) => {
  const [showPayment, setShowPayment] = useState(false);

  if (!isOpen) return null;

  const handleUpgradeClick = () => {
    if (!user) {
      alert('Please sign in to upgrade to premium');
      return;
    }
    setShowPayment(true);
  };

  const handlePaymentSuccess = () => {
    setShowPayment(false);
    onClose();
    if (onUpgradeSuccess) onUpgradeSuccess();
    // Reload to refresh premium status
    setTimeout(() => window.location.reload(), 500);
  };

  const features = [
    { icon: Sparkles, text: 'Unlimited coaching sessions' },
    { icon: Zap,      text: 'Advanced AI insights & analysis' },
    { icon: Star,     text: 'Exclusive affirmation packs' },
    { icon: Check,    text: 'Priority support' },
    { icon: Check,    text: 'Ad-free experience' },
    { icon: Check,    text: 'Export journal entries' },
  ];

  return (
    <>
      {/* Feature overview modal */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-white w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl">
          
          {/* Header */}
          <div className="relative bg-gradient-to-br from-dream-200 via-blush-100 to-lavender-200 p-5">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 w-8 h-8 bg-white/40 hover:bg-white/60 rounded-full flex items-center justify-center transition-colors"
            >
              <X className="w-4 h-4 text-pearl-700" />
            </button>
            <div className="flex items-center justify-center mb-3">
              <div className="w-12 h-12 bg-gradient-to-br from-dream-500 to-lavender-500 rounded-full flex items-center justify-center shadow-dream">
                <Crown className="w-6 h-6 text-white" />
              </div>
            </div>
            <h2 className="text-xl font-bold text-center text-pearl-900 mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              Upgrade to Premium
            </h2>
            <p className="text-pearl-600 text-center text-xs">
              {feature ? `Unlock ${feature} and more` : 'Unlock unlimited sessions and all features'}
            </p>
          </div>

          {/* Content */}
          <div className="p-5">
            {/* Price */}
            <div className="bg-gradient-to-br from-dream-50 to-lavender-50 border-2 border-dream-200 rounded-2xl p-4 mb-4 text-center">
              <div className="flex items-baseline justify-center">
                <span className="text-4xl font-bold text-pearl-900">$9.95</span>
                <span className="text-pearl-500 text-sm ml-1">/month</span>
              </div>
              <p className="text-pearl-400 text-xs mt-1">Cancel anytime · 14-day money-back guarantee</p>
            </div>

            {/* Features */}
            <div className="space-y-2.5 mb-5">
              {features.map((item, i) => {
                const Icon = item.icon;
                return (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-dream-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon className="w-3 h-3 text-dream-600" />
                    </div>
                    <span className="text-pearl-700 text-sm">{item.text}</span>
                  </div>
                );
              })}
            </div>

            {/* CTA */}
            <button
              onClick={handleUpgradeClick}
              className="w-full py-3 px-4 rounded-full bg-gradient-to-r from-dream-500 to-lavender-500 text-white font-semibold text-sm hover:from-dream-600 hover:to-lavender-600 transition-all shadow-dream flex items-center justify-center gap-2 mb-2"
            >
              <Crown className="w-4 h-4" />
              Upgrade Now
            </button>
            <button
              onClick={onClose}
              className="w-full text-pearl-400 hover:text-pearl-600 font-medium py-2 text-xs transition-colors"
            >
              Maybe Later
            </button>
          </div>
        </div>
      </div>

      {/* Stripe embedded payment modal */}
      <StripePaymentModal
        isOpen={showPayment}
        onClose={() => setShowPayment(false)}
        onSuccess={handlePaymentSuccess}
        user={user}
      />
    </>
  );
};

// ============================================================
// USAGE LIMIT WARNING
// ============================================================
export const UsageLimitWarning = ({ current, limit, feature, onUpgrade }) => {
  const percentage = (current / limit) * 100;
  const isNearLimit = percentage >= 66;
  const isAtLimit = current >= limit;

  if (!isNearLimit) return null;

  return (
    <div className={`rounded-2xl p-5 shadow-xl border-2 ${
      isAtLimit
        ? 'bg-gradient-to-br from-red-50 to-dream-50 border-red-300'
        : 'bg-gradient-to-br from-dream-50 to-lavender-50 border-dream-300'
    }`}>
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-md ${
          isAtLimit ? 'bg-red-500' : 'bg-gradient-to-br from-dream-500 to-lavender-500'
        }`}>
          <Lock className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h4 className={`font-bold text-lg mb-1 ${isAtLimit ? 'text-red-900' : 'text-dream-900'}`}>
            {isAtLimit ? `${feature} Limit Reached` : `Almost at your ${feature} limit`}
          </h4>
          <p className={`text-sm mb-3 ${isAtLimit ? 'text-red-700' : 'text-dream-700'}`}>
            {isAtLimit
              ? `You've used all ${limit} free ${feature.toLowerCase()}. Upgrade to continue!`
              : `You've used ${current} of ${limit} free ${feature.toLowerCase()}.`}
          </p>
          <div className="w-full bg-white rounded-full h-2.5 mb-4 overflow-hidden border border-dream-200">
            <div
              className={`h-full transition-all rounded-full ${
                isAtLimit
                  ? 'bg-gradient-to-r from-red-400 to-red-500'
                  : 'bg-gradient-to-r from-dream-400 to-lavender-400'
              }`}
              style={{ width: `${Math.min(percentage, 100)}%` }}
            />
          </div>
          <button
            onClick={onUpgrade}
            className="bg-gradient-to-r from-dream-500 to-lavender-500 text-white font-semibold py-2.5 px-5 rounded-full flex items-center gap-2 text-sm shadow-dream hover:from-dream-600 hover:to-lavender-600 transition-all"
          >
            <Crown className="w-4 h-4" />
            Upgrade to Premium
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// PREMIUM BADGE
// ============================================================
export const PremiumBadge = () => (
  <span className="inline-flex items-center gap-1 bg-gradient-to-r from-dream-500 to-lavender-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">
    <Crown className="w-3 h-3" />
    PRO
  </span>
);
